import * as React from 'react';
import { useEffect } from 'react';

import { logErrorService } from '../index';
import analytics from './analytics';

// Source: https://developers.google.com/youtube/iframe_api_reference

const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';
const PROGRESS_POLL_MS = 1000;
const PROGRESS_THRESHOLDS = [25, 50, 75, 95] as const;

export const YT_PLAYER_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

export interface YTPlayer {
  getCurrentTime: () => number;
  getDuration: () => number;
  destroy: () => void;
  addEventListener: (event: string, handler: (e: YTStateChangeEvent) => void) => void;
  removeEventListener: (event: string, handler: (e: YTStateChangeEvent) => void) => void;
}

export interface YTStateChangeEvent {
  data: number;
  target: YTPlayer;
}

export interface YTPlayerOptions {
  events?: {
    onReady?: (e: { target: YTPlayer }) => void;
    onStateChange?: (e: YTStateChangeEvent) => void;
  };
}

export interface YTNamespace {
  Player: new (elementId: string | HTMLElement, options?: YTPlayerOptions) => YTPlayer;
  PlayerState: typeof YT_PLAYER_STATE;
}

interface YouTubeWindow extends Window {
  YT?: YTNamespace;
  onYouTubeIframeAPIReady?: () => void;
}

let apiLoadPromise: Promise<YTNamespace> | null = null;

export function loadYouTubeIframeApi(): Promise<YTNamespace> {
  const win = window as YouTubeWindow;

  if (apiLoadPromise) {
    return apiLoadPromise;
  }

  if (win.YT && win.YT.Player) {
    apiLoadPromise = Promise.resolve(win.YT);
    return apiLoadPromise;
  }

  apiLoadPromise = new Promise<YTNamespace>((resolve, reject) => {
    const previousReady = win.onYouTubeIframeAPIReady;
    win.onYouTubeIframeAPIReady = () => {
      if (previousReady) {
        try {
          previousReady();
        } catch (err) {
          logErrorService(err);
        }
      }
      if (win.YT && win.YT.Player) {
        resolve(win.YT);
      } else {
        reject(new Error('YouTube IFrame API loaded but YT.Player is unavailable'));
      }
    };

    const existing = document.querySelector(`script[src="${IFRAME_API_SRC}"]`);
    if (existing) {
      return;
    }

    const script = document.createElement('script');
    script.src = IFRAME_API_SRC;
    script.async = true;
    script.onerror = () => {
      script.remove();
      apiLoadPromise = null;
      reject(new Error('Failed to load YouTube IFrame API'));
    };
    document.head.appendChild(script);
  });

  return apiLoadPromise;
}

export function attachYouTubeProgressTracking(player: YTPlayer, videoName: string): () => void {
  let hasFiredPlay = false;
  const firedThresholds = new Set<number>();
  let pollId: ReturnType<typeof setInterval> | null = null;

  const checkProgress = () => {
    try {
      const duration = player.getDuration();
      const currentTime = player.getCurrentTime();
      if (!duration || duration <= 0) {
        return;
      }
      const percent = (currentTime / duration) * 100;
      for (const threshold of PROGRESS_THRESHOLDS) {
        if (percent >= threshold && !firedThresholds.has(threshold)) {
          firedThresholds.add(threshold);
          analytics.trackPredefinedEvent('WatchVideo', { percent: threshold, name: videoName });
        }
      }
    } catch (err) {
      logErrorService(err);
    }
  };

  const startPolling = () => {
    if (pollId === null) {
      pollId = setInterval(checkProgress, PROGRESS_POLL_MS);
    }
  };

  const stopPolling = () => {
    if (pollId !== null) {
      clearInterval(pollId);
      pollId = null;
    }
  };

  const handleStateChange = (event: YTStateChangeEvent) => {
    try {
      if (event.data === YT_PLAYER_STATE.PLAYING) {
        if (!hasFiredPlay) {
          hasFiredPlay = true;
          analytics.trackPredefinedEvent('PlayVideo', { name: videoName });
        }
        startPolling();
      } else if (event.data === YT_PLAYER_STATE.ENDED) {
        checkProgress();
        stopPolling();
      } else {
        stopPolling();
      }
    } catch (err) {
      logErrorService(err);
    }
  };

  player.addEventListener('onStateChange', handleStateChange);

  return () => {
    stopPolling();
    try {
      player.removeEventListener('onStateChange', handleStateChange);
    } catch (err) {
      logErrorService(err);
    }
  };
}

export function useYouTubePlayerTracking(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  options: { videoKey: string; trackingName: string }
): void {
  const { videoKey, trackingName } = options;

  useEffect(() => {
    let detach: (() => void) | null = null;
    let player: YTPlayer | null = null;
    let cancelled = false;

    loadYouTubeIframeApi()
      .then(YT => {
        if (cancelled || !iframeRef.current) return;
        player = new YT.Player(iframeRef.current, {
          events: {
            onReady: ({ target }) => {
              if (cancelled) return;
              detach = attachYouTubeProgressTracking(target, trackingName);
            },
          },
        });
      })
      .catch(err => {
        logErrorService(err);
      });

    return () => {
      cancelled = true;
      if (detach) {
        detach();
      }
      if (player) {
        try {
          player.destroy();
        } catch (err) {
          logErrorService(err);
        }
      }
    };
    // iframeRef is stable across renders; rebind only when the underlying video or its tracking label changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoKey, trackingName]);
}

export function _resetYouTubeApiLoadPromiseForTests() {
  apiLoadPromise = null;
}
